import { SearchResults } from "../../Shared/scriptts/interfaces";
import { params, aliasMapping, searchFilters, brandedSearch, executeSearchUrl, displayResults } from '../search-main';
import { addParam, addToArrayParam, isBrandedSearchFilter, removeFromArrayParam, removeParam } from '../search-filters-main';

declare var gIsSearchFiltersEnabled: boolean;
declare var gAliasMapping: any;

/**
 * Display filters that have no partial specified in .Net code
 * @param data The data returned from the search API
 */
export function displayNoPartialFilters(data: SearchResults) {
    if (gIsSearchFiltersEnabled) {
        displaySearchFiltersTemp(data);
    }
    else {
        setFilterCounts(data);
        searchFilters.forEach(filter => {
            if (filter.Partial) {
                return;
            }
            const hasBrandedSearchValue = filterHasBrandedSearchValue(filter.FilterType);

            if (hasBrandedSearchValue) {
                document.querySelector<HTMLButtonElement>(`#select-all-${filter.FilterType}`)?.classList.add('hidden');
            }
            else {
                document.querySelector<HTMLButtonElement>(`#select-all-${filter.FilterType}`)?.addEventListener('click', toggleAllFilterItemsClicked);
            }

            document.querySelectorAll<HTMLInputElement>(`[name=${filter.FilterType}]`).forEach(input => {
                input.addEventListener('change', filterItemChanged);
                input.checked = params[aliasMapping[input.name]?.JOAQueryName?.toString()]?.indexOf(input.value) > -1;
                if (hasBrandedSearchValue) {
                    if (!isBrandedSearchFilter(input.name, input.value)) {
                        document.querySelector<HTMLElement>(`#li-${input.id}`)?.classList.add('hidden');
                    }
                }
            });
            const titleElem = document.querySelector<HTMLElement>(`#titleaccordion-${filter.FilterType}`);
            if (titleElem === null) {
                return;
            }
            const countIcon = document.querySelector<HTMLElement>(`#count-${filter.FilterType}`) || document.createElement('span');
            const propertyName = [aliasMapping[filter.FilterType]?.JOAQueryName]?.toString();
            countIcon.id = `count-${filter.FilterType}`;
            countIcon.className = params[propertyName]?.length > 0 ? 'bg-warning rounded-full py-1 px-2 ml-2' : 'bg-warning rounded-full py-1 px-2 ml-2 hidden';
            countIcon.innerText = params[propertyName]?.length.toString();
            if (!titleElem.contains(countIcon)) {
                titleElem.appendChild(countIcon);
            }
            setSelectAllText(filter.FilterType);
        });
    }
}

// todo: This becomes the new displayNoPartialFilters function when we no longer need the SearchFilters feature toggle.
function displaySearchFiltersTemp(data: SearchResults) {
    setFilterCounts(data);
    searchFilters.forEach(filter => {
        if (filter.Partial) {
            return;
        }
        const hasBrandedSearchValue = filterHasBrandedSearchValue(filter.FilterType);
        const applyButton = document.querySelector<HTMLButtonElement>(`#apply-filters-${filter.FilterType}`);
        const clearButton = document.querySelector<HTMLButtonElement>(`#clear-filters-${filter.FilterType}`);

        if (applyButton && !applyButton.dataset.listenerAttached) {
            applyButton.addEventListener('click', () => applyFiltersClicked(filter.FilterType));
            applyButton.dataset.listenerAttached = 'true';
        }

        if (clearButton && !clearButton.dataset.listenerAttached) {
            clearButton.addEventListener('click', () => clearFiltersClicked(filter.FilterType));
            clearButton.dataset.listenerAttached = 'true';
        }

        updateFilterButtonStates(filter.FilterType);

        document.querySelectorAll<HTMLInputElement>(`[name=${filter.FilterType}]`).forEach(input => {
            input.addEventListener('change', () => {
                if (hasBrandedSearchValue) {
                    document.querySelector<HTMLButtonElement>(`#clear-filters-${filter.FilterType}`)?.classList.add('hidden');
                    return;
                }
                const inputs = document.querySelectorAll<HTMLInputElement>(`[name=${filter.FilterType}]`);
                let anyChecked = false;
                inputs.forEach(i => {
                    if (i.checked) {
                        anyChecked = true;
                    }
                });
                if (anyChecked) {
                    document.querySelector<HTMLButtonElement>(`#clear-filters-${filter.FilterType}`)?.classList.remove('hidden');
                }
                else {
                    document.querySelector<HTMLButtonElement>(`#clear-filters-${filter.FilterType}`)?.classList.add('hidden');
                }
            });            
            input.checked = isInputApplied(input);
            if (hasBrandedSearchValue) {
                if (!isBrandedSearchFilter(getInputFilterType(input), input.value)) {
                    document.querySelector<HTMLElement>(`#li-${input.id}`)?.classList.add('hidden');
                }
                else {
                    input.disabled = true;
                }
            }
        });
    });
}

function getInputFilterType(input: HTMLInputElement): string {
    return input.dataset.filterType || input.name;
}

function getInputPropertyName(input: HTMLInputElement): string {
    return aliasMapping[getInputFilterType(input)]?.JOAQueryName?.toString() || '';
}

function isInputApplied(input: HTMLInputElement): boolean {
    const propertyName = getInputPropertyName(input);
    if (!propertyName) {
        return false;
    }

    const param = params[propertyName];
    if (Array.isArray(param)) {
        return param.indexOf(input.value) > -1;
    }

    if (param === undefined || param === null) {
        return false;
    }

    return param.toString().toLowerCase() === input.value.toLowerCase();
}

function setInputParam(input: HTMLInputElement) {
    const propertyName = getInputPropertyName(input);
    if (!propertyName) {
        return;
    }

    if (Array.isArray(params[propertyName])) {
        if (input.checked) {
            addParam(propertyName, input.value);
        }
        else {
            removeParam(propertyName, input.value);
        }

        return;
    }

    params[propertyName] = input.checked ? input.value.toLowerCase() === 'true' : null;
}

function getAppliedInputs(filterType: string): HTMLInputElement[] {
    return Array.from(document.querySelectorAll<HTMLInputElement>(`[name=${filterType}]`))
        .filter(input => isInputApplied(input));
}

function setCountIconDisplayForFilter(filterType: string) {
    const countIcon = document.querySelector<HTMLElement>(`#count-${filterType}`);
    if (countIcon === null) {
        return;
    }

    const count = getAppliedInputs(filterType).length;
    if (count > 0) {
        countIcon.innerText = count.toString();
        countIcon.classList.remove('hidden');
    }
    else {
        countIcon.innerText = '0';
        countIcon.classList.add('hidden');
    }
}

function toggleAllFilterItemsClicked(evt: Event) {
    const toggleButton = <HTMLButtonElement>(evt.target);
    const filterType = toggleButton.getAttribute('data-filter-type') ?? '';
    const dataSelected = toggleButton.getAttribute('data-selected') ?? 'false';
    const filterInputs = document.querySelectorAll<HTMLInputElement>(`[name=${filterType}]`);
    const paramProperty = aliasMapping[filterInputs[0]?.name]?.JOAQueryName?.toString();

    toggleButton.setAttribute('data-selected', dataSelected === 'true' ? 'false' : 'true');
    document.querySelectorAll<HTMLInputElement>(`[name=${filterType}]`).forEach(input => {
        input.checked = !(dataSelected === 'true');
        if (input.checked) {
            addToArrayParam(paramProperty, input.value);
        }
        else {
            removeFromArrayParam(paramProperty, input.value);
        }
    });

    setSelectAllText(filterType);
    displayResults(executeSearchUrl, false, true);
    setCountIconDisplay(paramProperty, filterType);
}

function applyFiltersClicked(filterType: string) {
    const itemList = document.querySelectorAll<HTMLInputElement>(`[name=${filterType}]`);

    if (!itemList || itemList.length === 0) {
        return;
    }

    itemList.forEach(input => {
        setInputParam(input);
    });
    params.Page = '1';
    displayResults(executeSearchUrl, false, true);
    setCountIconDisplayForFilter(filterType);
    updateFilterButtonStates(filterType);
}

function clearFiltersClicked(filterType: string) {
    if (filterHasBrandedSearchValue(filterType)) {
        document.querySelector<HTMLButtonElement>(`#clear-filters-${filterType}`)?.classList.add('hidden');
        return;
    }

    const itemList = document.querySelectorAll<HTMLInputElement>(`[name=${filterType}]`);

    if (!itemList || itemList.length === 0) {
        return;
    }

    itemList.forEach(input => {
        input.checked = false;
        setInputParam(input);
    });
    params.Page = '1';
    displayResults(executeSearchUrl, false, true);
    setCountIconDisplayForFilter(filterType);
    updateFilterButtonStates(filterType);
    document.querySelector<HTMLButtonElement>(`#clear-filters-${filterType}`)?.classList.add('hidden');
}

function updateFilterButtonStates(filterType: string) {
    const filterButton = document.querySelector<HTMLButtonElement>(`#filter-button-${filterType}`);
    const buttonTextElem = document.querySelector<HTMLElement>(`#filter-button-text-${filterType}`);
    if (!filterButton || !buttonTextElem) {
        return;
    }

    const appliedInputs = getAppliedInputs(filterType);

    if (appliedInputs.length > 0) {
        filterButton.classList.add('button-search-filter-applied');
        filterButton.classList.remove('button-search-filter');
        filterButton.querySelectorAll<HTMLElement>('svg').forEach(icon => {
            icon.classList.add('fill-white');
            icon.classList.remove('fill-gray-darkest');
        });
        buttonTextElem.innerText = appliedInputs[0].getAttribute('data-display-name') || '';
        if (appliedInputs.length > 1) {
            buttonTextElem.innerText += ` +${appliedInputs.length - 1}`;
        }

        if (!filterHasBrandedSearchValue(filterType)) {
            document.querySelector<HTMLButtonElement>(`#clear-filters-${filterType}`)?.classList.remove('hidden');
        }
    }
    else {
        filterButton.classList.remove('button-search-filter-applied');
        filterButton.classList.add('button-search-filter');
        filterButton.querySelectorAll<HTMLElement>('svg').forEach(icon => {
            icon.classList.remove('fill-white');
            icon.classList.add('fill-gray-darkest');
        });
        buttonTextElem.innerText = gAliasMapping[filterType]?.DisplayName || '';
        document.querySelector<HTMLButtonElement>(`#clear-filters-${filterType}`)?.classList.add('hidden');
    }
}

function filterHasBrandedSearchValue(filterType: string): boolean {
    const filterTypes = [...new Set(Array.from(document.querySelectorAll<HTMLInputElement>(`[name=${filterType}]`)).map(input => getInputFilterType(input)))];

    return filterTypes.some(itemFilterType => {
        const inBrandedSearch = brandedSearch && brandedSearch.SelectedFilters.find(f => f == itemFilterType) !== undefined;
        return inBrandedSearch && brandedSearch[aliasMapping[itemFilterType].JOAQueryName] !== undefined;
    });
}

function filterItemChanged(evt: Event) {
    const itemElem = <HTMLInputElement>(evt.target);
    const filterType = itemElem.name ?? '';
    const propertyName = aliasMapping[itemElem.name]?.JOAQueryName?.toString();
    if (!propertyName) {
        return;
    }
    if (itemElem.checked) {
        let allSelected = 'true';
        if (!params[propertyName]) {
            params[propertyName] = [itemElem.value];
        }
        else if (params[propertyName].indexOf(itemElem.value) === -1) {
            params[propertyName].push(itemElem.value);
        }
        const toggleButton = document.querySelector<HTMLButtonElement>(`#select-all-${filterType}`);
        if (!toggleButton) {
            return;
        }
        document.querySelectorAll<HTMLInputElement>(`[name=${filterType}]`).forEach(input => {
            if (!input.checked) {
                allSelected = 'false';
            }
        });
        toggleButton.setAttribute('data-selected', allSelected);
        setSelectAllText(filterType);
    }
    else {
        params[propertyName]?.splice(params[propertyName]?.indexOf(itemElem.value), 1);
        const toggleButton = document.querySelector<HTMLButtonElement>(`#select-all-${filterType}`);
        if (toggleButton) {
            toggleButton.setAttribute('data-selected', 'false');
            setSelectAllText(filterType);
        }
    }
    params.Page = '1';
    displayResults(executeSearchUrl, false, true);
    setCountIconDisplay(propertyName, filterType);
}

function setCountIconDisplay(paramProperty, filterType: string) {
    const countIcon = document.querySelector<HTMLElement>(`#count-${filterType}`);
    if (countIcon === null) {
        return;
    }
    const count = params[paramProperty]?.length;
    if (count > 0) {
        countIcon.innerText = count.toString();
        countIcon.classList.remove('hidden');
    }
    else {
        countIcon.innerText = '0';
        countIcon.classList.add('hidden');
    }
}

function setSelectAllText(filterType: String) {
    const elem = document.querySelector<HTMLButtonElement>(`#select-all-${filterType}`);
    if (!elem) {
        return;
    }
    const inputs = document.querySelectorAll<HTMLInputElement>(`[name=${filterType}]`);
    let isAllSelected = true;

    for (let i = 0; i < inputs.length; i++) {
        if (!inputs[i].checked) {
            isAllSelected = false;
            break;
        }
    }
    if (isAllSelected) {
        elem.setAttribute('data-selected', 'true');
        elem.innerText = 'Deselect all';
        elem.title = elem.title.replace('Select', 'Deselect');
    }
    else {
        elem.setAttribute('data-selected', 'false');
        elem.innerText = 'Select all';
        elem.title = elem.title.replace('Deselect', 'Select');
    }
}

function setFilterCounts(data: SearchResults) {
    searchFilters.forEach((filter) => {
        if (filter.Partial) {
            return;
        }

        if (filter.FilterGroup) {
            filter.FilterGroup.forEach((group, groupIndex) => {
                group.Count = 0;
                group.Items?.forEach(item => {
                    const itemFilterType = item.FilterType || filter.FilterType;
                    const codeCounts = data.f[aliasMapping[itemFilterType].Abbreviation];
                    item.Count = codeCounts && codeCounts.hasOwnProperty(item.Value) ? codeCounts[item.Value] : 0;
                    group.Count += item.Count;
                    const itemCountElem = document.querySelector<HTMLElement>(`#item-count-${filter.FilterType}-${item.Value}`);
                    if (!itemCountElem) {
                        return;
                    }
                    itemCountElem.innerText = `(${item.Count})`;
                });
                const groupCountElem = document.querySelector<HTMLElement>(`#group-count-${filter.FilterType}-${groupIndex}`);
                if (!groupCountElem) {
                    return;
                }
                groupCountElem.innerText = `(${group.Count})`;
            });
        }
        else {
            filter.Items.forEach(item => {
                const itemFilterType = item.FilterType || filter.FilterType;
                const codeCounts = data.f[aliasMapping[itemFilterType].Abbreviation];
                const itemCountElem = document.querySelector<HTMLElement>(`#item-count-${filter.FilterType}-${item.Value}`);
                if (!itemCountElem) {
                    return;
                }
                item.Count = codeCounts && codeCounts.hasOwnProperty(item.Value) ? codeCounts[item.Value] : 0;
                itemCountElem.innerText = `(${item.Count})`;
            });
        }
    });
}
